import { Send } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/v2/button";
import {
  redeliverApplyErrorKey,
  useRedeliverApplyToCockpit,
} from "@/features/integrations/modules/applies/api";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";
import useToast from "@/lib/hooks/useToast";

type RedeliverApplyButtonProps = {
  applyId: string;
};

export function RedeliverApplyButton({ applyId }: RedeliverApplyButtonProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { canEdit } = usePermissionMatrix();
  const redeliver = useRedeliverApplyToCockpit(applyId);

  // Redelivery pushes a candidate into a customer's ATS, so it is SuperAdmin/Admin only — the same gate
  // CockpitAppliesController authorizes on.
  if (!canEdit("integrations")) return null;

  const handleRedeliver = () =>
    redeliver.mutate(undefined, {
      onSuccess: () =>
        showToast(t("integrations.cockpit.applies.detail.redeliver.queued"), "success"),
      onError: (error) => showToast(t(redeliverApplyErrorKey(error)), "error"),
    });

  return (
    <Button
      variant="outline"
      size="sm"
      loading={redeliver.isPending}
      // Delivery is queued, not completed, on 202: the match ID only shows up on a later refetch. Staying
      // disabled after a success keeps an impatient second click from queueing the same candidate twice.
      disabled={redeliver.isSuccess}
      onClick={handleRedeliver}
    >
      {!redeliver.isPending && <Send className="h-4 w-4" />}
      {t("integrations.cockpit.applies.detail.redeliver.action")}
    </Button>
  );
}
